/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import { getNumPages } from "../selectors/commonAppSelectors";

export const unionOfRects = rects => {
  if (!rects || !rects.length) return {};

  const unionRect = {
    left: Number.MAX_SAFE_INTEGER,
    top: Number.MAX_SAFE_INTEGER,
    right: Number.MIN_SAFE_INTEGER,
    bottom: Number.MIN_SAFE_INTEGER
  };
  rects.forEach(rect => {
    unionRect.left = Math.min(Math.min(rect.left, rect.right), unionRect.left);
    unionRect.top = Math.min(Math.min(rect.top, rect.bottom), unionRect.top);
    unionRect.right = Math.max(
      Math.max(rect.right, rect.left),
      unionRect.right
    );
    unionRect.bottom = Math.max(
      Math.max(rect.bottom, rect.top),
      unionRect.bottom
    );
  });
  return unionRect;
};

export const cloneJSON = jsonObj => JSON.parse(JSON.stringify(jsonObj));

export const isValidPageIndex = (stores, pageIndex) =>
  pageIndex >= 0 && pageIndex < getNumPages(stores);

export const isFunction = value => {
  const type = typeof value;
  return value != null && type === "function";
};

export const isObject = val =>
  Object.prototype.toString.call(val) === "[object Object]";

export const mergeDeep = (target, source) => {
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) target[key] = source[key];
        else target[key] = mergeDeep(target[key], source[key]);
      } else {
        target[key] = source[key];
      }
    });
  }
  return target;
};

const trimArray = arr => {
  const len = arr.length;
  let i;
  for (i = len - 1; i >= 0; i -= 1) {
    if (arr[i] > 0) {
      break;
    }
  }
  arr.splice(i + 1);
};

/**
 * This function returns value in the form (v1 - v2)
 *
 * 1) If v1 > v2, return 1
 * 2) If v1 < v2, return -1
 * 3) If v1 = v2, return 0
 *
 * - compareVersion("0.20.7", "0.20.8") = -1
 * - compareVersion("0.20.9", "0.20.8") = 1
 * - compareVersion("0.20.08", "0.20.8") = 0
 *
 * @param {String} version1
 * @param {String} version2
 */
const compareVersion = (version1, version2) => {
  const v1 = version1.split(".").map(x => parseInt(x, 10));
  const v2 = version2.split(".").map(x => parseInt(x, 10));
  trimArray(v1);
  trimArray(v2);
  const iterations = Math.min(v1.length, v2.length);
  for (let i = 0; i < iterations; i += 1) {
    if (v1[i] > v2[i]) return 1;
    if (v1[i] < v2[i]) return -1;
  }
  if (v1.length === v2.length) {
    return 0;
  } else if (v1.length < v2.length) {
    return -1;
  }
  return 1;
};

export const isVersionGreater = (v1, v2) => compareVersion(v1, v2) === 1;
export const isVersionLesser = (v1, v2) => compareVersion(v1, v2) === -1;
export const isVersionEqual = (v1, v2) => compareVersion(v1, v2) === 0;

/**
 *
 * @param {string} selector
 * @param {number} maxTime
 * @returns
 */
export const waitForDOMElement = function waitForDOMElement(
  selector,
  maxTime = 1000
) {
  const waitForPromise = new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const element = document.querySelector(selector);

      if (element !== null) {
        clearInterval(interval);
        resolve(element);
      }
    }, 250);

    setTimeout(() => {
      clearInterval(interval);
      reject(new Error(`Error - ${selector} not found in DOM`));
    }, maxTime);
  });

  return waitForPromise;
};
